<template>
  <div>
    <!-- ── Detail / Approval Modal ───────────────────────────────────────────── -->
    <b-modal
      id="modal-excess-auth"
      ref="myModal"
      centered
      no-close-on-backdrop
      no-close-on-esc
      size="lg"
      hide-footer
      hide-header
    >
      <template #default="{}">
        <div class="modal-inner">
          <!-- Header -->
          <div class="modal-head">
            <div class="d-flex align-items-center" style="gap: 10px">
              <div class="modal-icon" :class="modalIconClass">
                <feather-icon :icon="modalIcon" size="16" />
              </div>
              <div>
                <div class="modal-title">
                  {{
                    modalMode === "bulk"
                      ? `${selectedIds.length} Bale${selectedIds.length > 1 ? "s" : ""} — ${bulkAction === "approve" ? "Approve" : "Reject"}`
                      : `Excess Bale ${activeBale ? activeBale.barcode : ""}`
                  }}
                </div>
                <div v-if="activeBale && modalMode === 'detail'" class="modal-sub">
                  {{ activeBale.source && activeBale.source.name ? activeBale.source.name : "—" }}
                  →
                  {{ activeBale.destination && activeBale.destination.name ? activeBale.destination.name : "—" }}
                  <span v-if="activeBale.transport"> · TF {{ activeBale.tfNuumber }}</span>
                  · Missed by {{ missedByName(activeBale) }}
                </div>
                <div v-if="modalMode === 'bulk'" class="modal-sub">
                  Bulk {{ bulkAction }} — decision will apply to all selected bales
                </div>
              </div>
            </div>
            <feather-icon
              icon="XIcon"
              size="16"
              class="cursor-pointer text-secondary"
              @click="$bvModal.hide('modal-excess-auth')"
            />
          </div>

          <div class="modal-body-inner">
            <!-- Bulk info banner -->
            <b-alert v-if="modalMode === 'bulk'" show variant="info" class="mb-1">
              <div class="alert-body" style="font-size: 12px">
                <strong>{{ selectedIds.length }} bale{{ selectedIds.length > 1 ? "s" : "" }}</strong>
                selected for {{ bulkAction === "approve" ? "approval" : "rejection" }}.
                The same reason will be recorded against each bale in the audit log.
              </div>
            </b-alert>

            <!-- Single bale detail -->
            <template v-if="modalMode === 'detail' && activeBale">
              <!-- Info grid -->
              <div class="info-grid mb-1">
                <div class="info-cell">
                  <div class="info-label">Barcode</div>
                  <div class="info-value font-monospace" style="font-size: 13px">
                    {{ activeBale.barcode }}
                  </div>
                </div>
                <div class="info-cell">
                  <div class="info-label">TF received with</div>
                  <div class="info-value font-monospace" style="font-size: 13px">
                    <span v-if="activeBale.tfNumber">{{ activeBale.tfNumber }}</span>
                    <span v-else class="text-muted">—</span>
                  </div>
                </div>
                <div class="info-cell">
                  <div class="info-label">Origin warehouse</div>
                  <div class="info-value">
                    {{ activeBale.source && activeBale.source.name ? activeBale.source.name : "—" }}
                  </div>
                </div>
                <div class="info-cell">
                  <div class="info-label">Destination warehouse</div>
                  <div class="info-value">
                    {{ activeBale.destination && activeBale.destination.name ? activeBale.destination.name : "—" }}
                  </div>
                </div>
                <div class="info-cell">
                  <div class="info-label">Date received</div>
                  <div class="info-value">{{ activeBale.received_at | moment("DD/MM/YYYY HH:mm") }}</div>
                </div>
                <div class="info-cell">
                  <div class="info-label">Status</div>
                  <b-badge :variant="statusVariant(approvedLabel(activeBale.confirmed))" class="status-badge">
                    <feather-icon :icon="statusIcon(activeBale.confirmed)" size="10" class="mr-25" />
                    {{ approvedLabel(activeBale.confirmed) }}
                  </b-badge>
                </div>
                <div class="info-cell" style="grid-column: span 2">
                  <div class="info-label">Missed by (scanner / loader who omitted this bale from TF)</div>
                  <div class="info-value">{{ missedByName(activeBale) }}</div>
                </div>
              </div>

              <!-- Weight analysis -->
              <div class="section-head">Weight analysis</div>
              <b-row class="mb-1">
                <b-col cols="6" md="3">
                  <div class="weight-card wc-blue">
                    <div class="wc-label">Purchased</div>
                    <div class="wc-value">
                      {{ fmtKg(activeBale.mass) }}
                    </div>
                  </div>
                </b-col>
                <b-col cols="6" md="3">
                  <div class="weight-card wc-amber">
                    <div class="wc-label">Cromis recent</div>
                    <div class="wc-value">
                      {{ fmtKg(activeBale.last) }}
                    </div>
                  </div>
                </b-col>
                <b-col cols="6" md="3">
                  <div class="weight-card wc-green">
                    <div class="wc-label">Received (dest / MTS)</div>
                    <div class="wc-value">
                      {{ fmtKg(activeBale.received) }}
                    </div>
                  </div>
                </b-col>
                <b-col cols="6" md="3">
                  <div class="weight-card" :class="Math.abs(recvVariance(activeBale)) > 2 ? 'wc-red' : 'wc-green'">
                    <div class="wc-label">Cromis vs received</div>
                    <div class="wc-value" :class="Math.abs(recvVariance(activeBale)) > 2 ? 'text-danger' : 'text-success'">
                      {{ formatVariance(recvVariance(activeBale)) }}
                    </div>
                  </div>
                </b-col>
              </b-row>

              <!-- Variance summary cards -->
              <b-row class="mb-1">
                <b-col cols="12" md="6">
                  <div class="variance-card" :class="purchVariance(activeBale) > 0 ? 'vc-warn' : 'vc-danger'">
                    <div class="vc-title" :class="purchVariance(activeBale) > 0 ? 'text-warning' : 'text-danger'">
                      Purchased vs received variance
                    </div>
                    <div class="vc-value" :class="purchVariance(activeBale) > 0 ? 'text-warning' : 'text-danger'">
                      {{ formatVariance(purchVariance(activeBale)) }}
                    </div>
                    <div class="vc-sub">
                      {{ purchVariance(activeBale) > 0 ? "Received weight exceeds purchased" : "Weight lost from purchase" }}
                    </div>
                  </div>
                </b-col>
                <b-col cols="12" md="6">
                  <div class="variance-card" :class="Math.abs(recvVariance(activeBale)) > 2 ? 'vc-danger' : 'vc-success'">
                    <div class="vc-title" :class="Math.abs(recvVariance(activeBale)) > 2 ? 'text-danger' : 'text-success'">
                      Cromis vs received {{ Math.abs(recvVariance(activeBale)) > 2 ? "discrepancy" : "match" }}
                    </div>
                    <div class="vc-value" :class="Math.abs(recvVariance(activeBale)) > 2 ? 'text-danger' : 'text-success'">
                      {{ formatVariance(recvVariance(activeBale)) }}
                    </div>
                    <div class="vc-sub">
                      {{ Math.abs(recvVariance(activeBale)) > 2 ? "Exceeds 2 kg tolerance" : "Within 2 kg tolerance" }}
                    </div>
                  </div>
                </b-col>
              </b-row>

              <!-- Approval history -->
              <template v-if="activeBale.authLog && activeBale.authLog.length">
                <div class="section-head">Approval history</div>
                <div v-for="(entry, idx) in activeBale.authLog" :key="idx" class="log-entry">
                  <b-avatar size="30" :text="initials(entry.user)" variant="light-info" class="flex-shrink-0" />
                  <div class="flex-grow-1 ml-75">
                    <div class="log-name">
                      {{ entry.user }}
                      <span class="log-role">— {{ entry.role }}</span>
                    </div>
                    <div class="log-time">{{ entry.timestamp }}</div>
                    <div class="log-note">{{ entry.note }}</div>
                  </div>
                  <b-badge :variant="entry.action === 'approved' ? 'light-success' : 'light-danger'" class="ml-50">
                    {{ entry.action.charAt(0).toUpperCase() + entry.action.slice(1) }}
                  </b-badge>
                </div>
              </template>
            </template>

            <!-- Decision section — shown for pending bales and bulk actions -->
            <template v-if="(modalMode === 'detail' && activeBale && (isPending(activeBale.confirmed) || isRejected(activeBale.confirmed))) || modalMode === 'bulk'">
              <div class="section-head" :class="{ 'mt-1': modalMode === 'detail' }">
                {{ modalMode === "bulk" ? "Decision reason" : "Approval decision" }}
              </div>
              <div class="reason-block">
                <label class="reason-label">
                  Reason / notes
                  <span class="required-star">*</span>
                  <span class="reason-hint">(required — recorded in audit log)</span>
                </label>
                <b-form-textarea
                  v-model="authNote"
                  :state="reasonTouched && !authNote.trim() ? false : null"
                  :placeholder="modalMode === 'bulk'
                    ? 'Enter the reason for this bulk decision. It will be recorded against all selected bales...'
                    : 'Enter the reason for your approval decision. This will be recorded in the audit log and visible to management...'"
                  rows="4"
                  size="sm"
                  @input="reasonTouched = true"
                />
                <div v-if="reasonTouched && !authNote.trim()" class="reason-error-msg">
                  <feather-icon icon="AlertCircleIcon" size="12" class="mr-25" />
                  Please enter a reason before proceeding.
                </div>
              </div>
            </template>
          </div>

          <!-- Footer -->
          <div class="modal-foot">
            <template v-if="(modalMode === 'detail' && activeBale && (isPending(activeBale.confirmed)) || isRejected(activeBale.confirmed)) || modalMode === 'bulk'">
              <span class="foot-hint">Reason is mandatory for both approve and reject.</span>
              <b-button size="sm" variant="outline-secondary" @click="$bvModal.hide('modal-excess-auth')">Cancel</b-button>
              <b-button size="sm" variant="danger" :disabled="saving" @click="handleAction('reject')">
                <b-spinner v-if="saving && pendingAction === 'reject'" small class="mr-50" />
                {{ modalMode === "bulk" ? "Reject all" : "Reject Bale" }}
              </b-button>
              <b-button size="sm" variant="success" :disabled="saving" @click="handleAction('approve')">
                <b-spinner v-if="saving && pendingAction === 'approve'" small class="mr-50" />
                {{ modalMode === "bulk" ? "Approve all" : "Approve Bale" }}
              </b-button>
            </template>
            <template v-else>
              <b-button size="sm" variant="primary" @click="$bvModal.hide('modal-excess-auth')">Close</b-button>
            </template>
          </div>
        </div>
      </template>
    </b-modal>
    <!-- ./Detail / Approval Modal -->

    <!-- ── Page header ─────────────────────────────────────────────────────── -->
    <div class="page-section-header mb-1">
      <div class="d-flex align-items-center" style="gap: 12px">
        <div class="page-header-icon">
          <feather-icon icon="PackageIcon" size="20" />
        </div>
        <div>
          <h5 class="mb-25">Excess bale approval</h5>
          <small class="text-muted">
            Bales received at the destination warehouse that were not listed on the Transfer Form —
            approve to accept into inventory or reject
          </small>
        </div>
      </div>
    </div>

    <b-alert v-if="usingMockData" show variant="warning" class="demo-data-alert mb-1">
      <div class="alert-body d-flex align-items-center" style="gap: 8px; font-size: 12px">
        <feather-icon icon="InfoIcon" size="14" class="flex-shrink-0" />
        Showing sample data — the excess bales API is not reachable from this environment.
        Approve/reject actions on this screen will not persist.
      </div>
    </b-alert>

    <!-- ── Metric cards ────────────────────────────────────────────────────── -->
    <b-row class="mb-1">
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-blue mb-1 mb-md-0">
          <div class="metric-icon metric-icon-blue">
            <feather-icon icon="PackageIcon" size="16" />
          </div>
          <div class="metric-label">Total excess</div>
          <div class="metric-value text-primary" v-if="metrics">{{ metrics.registered.toLocaleString() }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-amber mb-1 mb-md-0">
          <div class="metric-icon metric-icon-amber">
            <feather-icon icon="ClockIcon" size="16" />
          </div>
          <div class="metric-label">Pending review</div>
          <div class="metric-value text-warning" v-if="metrics">{{ metrics.pending.toLocaleString() }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-green mb-1 mb-md-0">
          <div class="metric-icon metric-icon-green">
            <feather-icon icon="CheckCircleIcon" size="16" />
          </div>
          <div class="metric-label">Approved</div>
          <div class="metric-value text-success" v-if="metrics">{{ metrics.approved.toLocaleString() }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-red mb-0">
          <div class="metric-icon metric-icon-red">
            <feather-icon icon="XCircleIcon" size="16" />
          </div>
          <div class="metric-label">Rejected</div>
          <div class="metric-value text-danger" v-if="metrics">{{ metrics.rejected.toLocaleString() }}</div>
        </b-card>
      </b-col>
    </b-row>

    <!-- ── Main table card ─────────────────────────────────────────────────── -->
    <b-card no-body class="mb-0">
      <!-- Table top controls -->
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50 select-size-sm"
            />
            <label>entries</label>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search barcode, TF number..."
                size="sm"
              />
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Bulk action bar -->
      <div v-if="selectedIds.length > 0" class="bulk-bar">
        <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />
        <span>{{ selectedIds.length }} bale{{ selectedIds.length > 1 ? "s" : "" }} selected</span>
        <b-button size="sm" variant="success" @click="openBulkModal('approve')">Approve selected</b-button>
        <b-button size="sm" variant="danger" @click="openBulkModal('reject')">Reject selected</b-button>
        <b-button size="sm" variant="outline-secondary" @click="clearSelection">Clear selection</b-button>
      </div>

      <!-- Table -->
      <b-table
        ref="records"
        striped
        hover
        small
        class="position-relative excess-table"
        :items="fetch"
        responsive
        :fields="columns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No excess bales found"
        :sort-desc.sync="isSortDirDesc"
        :tbody-tr-class="rowClass"
        sticky-header="640px"
      >
        <!-- Checkbox header -->
        <template #head(selected)>
          <div style="display: flex; align-items: center; justify-content: center">
            <input
              ref="selectAllCheckbox"
              type="checkbox"
              :checked="allPendingOnPageSelected"
              :class="{ 'checkbox-indeterminate': somePendingOnPageSelected }"
              style="width: 15px; height: 15px; cursor: pointer; accent-color: #28a745"
              @change="toggleAll"
            />
          </div>
        </template>

        <template #cell(selected)="data">
          <div style="display: flex; align-items: center; justify-content: center">
            <input
              v-if="isPending(data.item.confirmed)"
              type="checkbox"
              :checked="selectedIds.includes(data.item.id)"
              style="width: 15px; height: 15px; cursor: pointer; accent-color: #28a745"
              @change="toggleRow(data.item.id)"
            />
          </div>
        </template>

        <template #cell(received_at)="data">
          <span style="font-size: 12px">{{ data.item.received_at | moment("DD/MM/YYYY") }}</span>
        </template>

        <template #cell(barcode)="data">
          <span class="font-monospace text-muted" style="font-size: 12px">
            {{ data.item.barcode }}
          </span>
        </template>

        <template #cell(tfNumber)="data">
          <span v-if="data.item.tfNumber !== null" class="font-monospace text-muted" style="font-size: 12px">
            {{ data.item.tfNumber }}
          </span>
          <b-badge v-else variant="light-secondary" style="font-size: 10px; padding: 2px 6px">No TF</b-badge>
        </template>

        <template #cell(purchased)="data">
          <span style="font-weight: 500">{{ fmtKg(data.item.mass) }}</span>
        </template>

        <template #cell(cromisWeight)="data">
          {{ fmtKg(data.item.last) }}
        </template>

        <template #cell(receivedWeight)="data">
          {{ fmtKg(data.item.received) }}
        </template>

        <template #cell(variance)="data">
          <span :class="Math.abs(recvVariance(data.item)) > 2 ? 'text-danger font-weight-bold' : 'text-muted'">
            {{ formatVariance(recvVariance(data.item)) }}
          </span>
          <b-badge
            v-if="Math.abs(recvVariance(data.item)) > 2"
            variant="danger"
            class="ml-25"
            style="font-size: 10px; padding: 2px 5px"
          >!</b-badge>
        </template>

        <template #cell(scanner)="data">
          <div class="d-flex align-items-center" style="gap: 6px">
            <b-avatar size="22" :text="initials(missedByName(data.item))" variant="light-secondary" class="flex-shrink-0" />
            <span class="text-muted" style="font-size: 12px">{{ missedByName(data.item) }}</span>
          </div>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="statusVariant(approvedLabel(data.item.confirmed))" class="status-badge">
            <feather-icon :icon="statusIcon(data.item.confirmed)" size="10" class="mr-25" />
            {{ approvedLabel(data.item.confirmed) }}
          </b-badge>
        </template>

        <!-- Every action first opens details modal; approve/reject are inside the modal -->
        <template #cell(action)="data">
          <div class="d-flex align-items-center" style="gap: 4px">
            <template v-if="isPending(data.item.confirmed)">
              <b-button
                size="sm"
                variant="success"
                style="padding: 2px 9px; font-size: 11px"
                @click="openDetailModal(data.item, 'approve')"
              >
                Approve
              </b-button>
              <b-button
                size="sm"
                variant="danger"
                style="padding: 2px 9px; font-size: 11px"
                @click="openDetailModal(data.item, 'reject')"
              >
                Reject
              </b-button>
            </template>
            <b-button
              size="sm"
              variant="outline-secondary"
              style="padding: 2px 9px; font-size: 11px"
              @click="openDetailModal(data.item)"
            >
              Details
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Table footer / pagination -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ from }} to {{ to }} of {{ totalRecords }} entries</span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRecords"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
              <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BTable, BPagination, BButton,
  BFormInput, BFormTextarea, BSpinner, BAlert, BBadge, BAvatar, BModal,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed, watch, onUnmounted, onMounted } from "@vue/composition-api";
import store from "@/store";
import excessStoreModule from "@/views/cromis/excess/excessStoreModule";
import useExcessList from "@/views/cromis/excess/useExcessList";
import { MOCK_EXCESS_METRICS } from "@/views/cromis/excess/excessMockData";
import { activated } from "vue-echarts";

export default {
  name: "ExcessBaleApproval",

  components: {
    BRow, BCol, BCard, BTable, BPagination, BButton,
    BFormInput, BFormTextarea, BSpinner, BAlert, BBadge, BAvatar, BModal, vSelect,
  },

  setup(props, context) {
    const metrics = ref(null);
    const STORE_MODULE = "cromis-excess";

    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, excessStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE)) store.unregisterModule(STORE_MODULE);
    });

    onMounted(async () => {
      await store.dispatch(`${STORE_MODULE}/metrics`)
        .then(response => { metrics.value = response.data.metrics })
        .catch(() => {
          // Backend endpoint not reachable yet — fall back to demo metrics.
          metrics.value = MOCK_EXCESS_METRICS;
        });
    });

    const {
      fetch, refetch,
      columns: composableColumns,
      perPage, currentPage, totalRecords, from, to, meta,
      perPageOptions, searchQuery, sortBy, isSortDirDesc,
      records, resolveStatusVariant, usingMockData,
    } = useExcessList();

    const columns = computed(() => {
      const base = Array.isArray(composableColumns) ? composableColumns : composableColumns.value || [];
      const filtered = base.filter(c => c.key !== "selected");
      return [
        { key: "selected", label: "", sortable: false, thStyle: "width:40px; min-width:40px; text-align:center" },
        ...filtered,
      ];
    });

    // ── Modal state ──────────────────────────────────────────────────────────
    const saving = ref(false);
    const pendingAction = ref(null);
    const authNote = ref("");
    const reasonTouched = ref(false);
    const activeBale = ref(null);
    const modalMode = ref("detail");
    const bulkAction = ref(null);
    const selectedIds = ref([]);

    function isPending(approved)    { return Number(approved) === 0; }
    function isApproved(approved)   { return Number(approved) === 1; }
    function isRejected(approved)   { return Number(approved) === -1; }

    const modalIcon = computed(() => {
      if (modalMode.value === "bulk")
        return bulkAction.value === "approve" ? "CheckCircleIcon" : "XCircleIcon";
      if (!activeBale.value) return "AlertCircleIcon";
      if (isApproved(activeBale.value.approved)) return "CheckCircleIcon";
      if (isRejected(activeBale.value.approved)) return "XCircleIcon";
      return "AlertCircleIcon";
    });

    const modalIconClass = computed(() => {
      if (modalMode.value === "bulk")
        return bulkAction.value === "approve" ? "modal-icon-success" : "modal-icon-danger";
      if (!activeBale.value) return "modal-icon-warning";
      if (isApproved(activeBale.value.approved)) return "modal-icon-success";
      if (isRejected(activeBale.value.approved)) return "modal-icon-danger";
      return "modal-icon-warning";
    });

    // ── Bulk selection ───────────────────────────────────────────────────────
    const pagePendingIds = computed(() => {
      if (!records.value || !records.value.length) return [];
      return records.value.filter(b => isPending(b.approved)).map(b => b.id);
    });

    const allPendingOnPageSelected = computed(() =>
      pagePendingIds.value.length > 0 &&
      pagePendingIds.value.every(id => selectedIds.value.includes(id))
    );

    const somePendingOnPageSelected = computed(() => {
      const count = pagePendingIds.value.filter(id => selectedIds.value.includes(id)).length;
      return count > 0 && count < pagePendingIds.value.length;
    });

    function toggleAll() {
      if (allPendingOnPageSelected.value) {
        selectedIds.value = selectedIds.value.filter(id => !pagePendingIds.value.includes(id));
      } else {
        pagePendingIds.value.forEach(id => {
          if (!selectedIds.value.includes(id)) selectedIds.value.push(id);
        });
      }
    }

    function toggleRow(id) {
      const idx = selectedIds.value.indexOf(id);
      if (idx > -1) selectedIds.value.splice(idx, 1);
      else selectedIds.value.push(id);
    }

    function clearSelection() { selectedIds.value = []; }

    watch(currentPage, () => { selectedIds.value = []; });

    const selectAllCheckbox = ref(null);
    watch([allPendingOnPageSelected, somePendingOnPageSelected], () => {
      if (selectAllCheckbox.value) {
        selectAllCheckbox.value.indeterminate = somePendingOnPageSelected.value;
      }
    });

    // ── Weight helpers ────────────────────────────────────────────────────────
    function fmtKg(v) {
      return parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " kg";
    }
    function recvVariance(b)   { return +(b.last - b.received).toFixed(2); }
    function purchVariance(b)  { return +(b.mass - b.received).toFixed(2); }
    function formatVariance(v) { return (v > 0 ? "+" : "") + parseFloat(v).toFixed(2) + " kg"; }

    // ── Display helpers ───────────────────────────────────────────────────────
    function statusVariant(status) {
      if (status === "Approved") return "light-success";
      if (status === "Rejected") return "light-danger";
      return "light-warning";
    }

    function approvedLabel(approved) {
      if (isApproved(approved)) return "Approved";
      if (isRejected(approved)) return "Rejected";
      return "Pending";
    }

    function statusIcon(approved) {
      if (isApproved(approved)) return "CheckIcon";
      if (isRejected(approved)) return "XIcon";
      return "ClockIcon";
    }

    function rowClass(item) {
      if (!item) return "";
      return Math.abs(recvVariance(item)) > 2 && isPending(item.approved) ? "row-breach" : "";
    }

    function missedByName(item) {
      if (!item.scanner || !item.scanner.person) return "—";
      const p = item.scanner.person;
      return p.middleName
        ? `${p.firstName} ${p.middleName} ${p.lastName}`
        : `${p.firstName} ${p.lastName}`;
    }

    function initials(name) {
      return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
    }

    // ── Reason validation ─────────────────────────────────────────────────────
    function validateReason() {
      reasonTouched.value = true;
      return !!authNote.value.trim();
    }

    // ── Modal open ────────────────────────────────────────────────────────────
    // hintAction: optional 'approve'|'reject' to pre-signal intent (UX only)
    function openDetailModal(item, hintAction) {
      if (!item.authLog) item.authLog = [];
      activeBale.value = item;
      modalMode.value = "detail";
      authNote.value = "";
      reasonTouched.value = false;
      context.root.$bvModal.show("modal-excess-auth");
    }

    function openBulkModal(action) {
      bulkAction.value = action;
      modalMode.value = "bulk";
      authNote.value = "";
      reasonTouched.value = false;
      context.root.$bvModal.show("modal-excess-auth");
    }

    // ── Approval action ───────────────────────────────────────────────────────
    async function handleAction(action) {
      if (!validateReason()) return;

      const approvedValue = action === "approve" ? 1 : -1;
      const note = authNote.value.trim();

      let itemsToProcess = [];
      if (modalMode.value === "bulk") {
        itemsToProcess = (records.value || []).filter(b => selectedIds.value.includes(b.id));
      } else {
        itemsToProcess = [activeBale.value];
      }

      const isBulk = itemsToProcess.length > 1;
      const actionLabel = action === "approve" ? "Approve" : "Reject";
      const actionPast = action === "approve" ? "approved" : "rejected";
      const confirmColor = action === "approve" ? "btn btn-success" : "btn btn-danger";

      const confirm = await context.root.$swal({
        title: `${actionLabel} ${isBulk ? itemsToProcess.length + " excess bales" : "this excess bale"}?`,
        html: `<div style="font-size:13px;color:#5e5873;text-align:left;margin-top:8px">
          <strong>Action:</strong> ${actionLabel}<br/>
          <strong>Bales:</strong> ${itemsToProcess.length}<br/>
          <strong>Reason:</strong> ${note}
        </div>`,
        icon: action === "approve" ? "question" : "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionLabel.toLowerCase()}`,
        cancelButtonText: "Cancel",
        customClass: { confirmButton: confirmColor, cancelButton: "btn btn-outline-secondary ml-1" },
        buttonsStyling: false,
      });

      if (!confirm.isConfirmed) return;

      pendingAction.value = action;
      saving.value = true;

      try {
        await Promise.all(
          itemsToProcess.map(b =>
            store.dispatch(`${STORE_MODULE}/confirmExcess`, { ticket_id: b.id, approved: approvedValue, remarks: note })
              .then(() => {
                refetch()
                store.dispatch(`${STORE_MODULE}/metrics`)
                  .then(response => { metrics.value = response.data.metrics })
                  .catch(error => { console.log(error) });
              })
              .catch(error => { console.log(error) })
          )
        );

        context.root.$bvModal.hide("modal-excess-auth");
        clearSelection();
        refetch();

        context.root.$swal({
          icon: action === "approve" ? "success" : "warning",
          title: `${isBulk ? itemsToProcess.length + " bales" : "Bale"} ${actionPast}`,
          text: `${itemsToProcess.length} bale${itemsToProcess.length > 1 ? "s" : ""} ${actionPast} successfully.`,
          showConfirmButton: false,
          timer: 2500,
        });
      } catch (error) {
        console.error("[ExcessBale] API error:", error);
        const msg = error.response?.status === 422
          ? Object.values(error.response.data.errors).flat().join("\n")
          : "Something went wrong. Please contact technical support.";
        context.root.$swal({
          icon: "error",
          title: error.response?.status === 422 ? "Validation error" : "Server error",
          text: msg,
          showConfirmButton: true,
          customClass: { confirmButton: "btn btn-danger" },
          buttonsStyling: false,
        });
      } finally {
        saving.value = false;
        pendingAction.value = null;
      }
    }

    return {
      fetch, refetch, columns,
      perPage, currentPage, totalRecords, from, to, meta,
      perPageOptions, searchQuery, sortBy, isSortDirDesc,
      records, resolveStatusVariant,
      saving, pendingAction, authNote, reasonTouched,
      activeBale, modalMode, bulkAction, modalIcon, modalIconClass,
      selectedIds, allPendingOnPageSelected, somePendingOnPageSelected,
      pagePendingIds, selectAllCheckbox,
      toggleAll, toggleRow, clearSelection,
      openDetailModal, openBulkModal, handleAction,
      isPending, isApproved, isRejected,
      fmtKg, recvVariance, purchVariance, formatVariance,
      statusVariant, approvedLabel, statusIcon, rowClass, missedByName, initials,
      metrics, usingMockData,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.page-section-header {
  padding-bottom: 0.85rem;
  border-bottom: 0.5px solid #ebe9f1;

  .page-header-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    background: linear-gradient(135deg, #e6f1fb, #d9ecf9);
    color: #185fa5;
  }
}

.demo-data-alert {
  border-radius: 8px;
  ::v-deep .alert-body { padding: 0.6rem 1rem; }
}

.status-badge {
  display: inline-flex; align-items: center;
}

// ── Metric cards ───────────────────────────────────────────────────────────
.metric-card {
  border-radius: 0.6rem;
  border-top: 3px solid transparent;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s;

  &:hover { box-shadow: 0 8px 28px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06); }

  ::v-deep .card-body { padding: 1rem 1.1rem; }

  &.metric-card-blue  { border-top-color: #185fa5; }
  &.metric-card-amber { border-top-color: #ef9f27; }
  &.metric-card-green { border-top-color: #639922; }
  &.metric-card-red   { border-top-color: #e24b4a; }

  .metric-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; margin-bottom: 10px;
    &.metric-icon-blue  { background: #e6f1fb; color: #185fa5; }
    &.metric-icon-amber { background: #faeeda; color: #854f0b; }
    &.metric-icon-green { background: #eaf3de; color: #3b6d11; }
    &.metric-icon-red   { background: #fcebeb; color: #a32d2d; }
  }

  .metric-label {
    font-size: 11px; color: #b9b9c3;
    text-transform: uppercase; letter-spacing: 0.35px; margin-bottom: 5px;
  }
  .metric-value { font-size: 24px; font-weight: 500; line-height: 1.2; }
}

// ── Bulk bar ───────────────────────────────────────────────────────────────
.bulk-bar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 9px 16px;
  background: #eaf3de; border-bottom: 0.5px solid #c0dd97;
  font-size: 12px; color: #27500a;
}

// ── Modal ──────────────────────────────────────────────────────────────────
.modal-inner { overflow: hidden; }

.modal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 0.5px solid #ebe9f1; background: #fafafa;

  .modal-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    &.modal-icon-success { background: #eaf3de; color: #3b6d11; }
    &.modal-icon-danger  { background: #fcebeb; color: #a32d2d; }
    &.modal-icon-warning { background: #faeeda; color: #854f0b; }
  }

  .modal-title { font-size: 14px; font-weight: 600; color: #5e5873; }
  .modal-sub   { font-size: 11px; color: #b9b9c3; margin-top: 1px; }
}

.modal-body-inner { padding: 18px 18px 4px; }

.modal-foot {
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding: 12px 18px;
  border-top: 0.5px solid #ebe9f1; background: #fafafa;
  .foot-hint { flex: 1; font-size: 11px; color: #b9b9c3; }
}

// ── Info grid ──────────────────────────────────────────────────────────────
.info-grid {
  display: grid; grid-template-columns: 1fr 1fr;
  border: 0.5px solid #ebe9f1; border-radius: 8px; overflow: hidden;

  .info-cell {
    padding: 10px 14px;
    border-right: 0.5px solid #ebe9f1; border-bottom: 0.5px solid #ebe9f1;
    &:nth-child(2n)        { border-right: none; }
    &:nth-last-child(-n+2) { border-bottom: none; }
  }
  .info-label {
    font-size: 10px; text-transform: uppercase;
    letter-spacing: 0.4px; color: #b9b9c3; margin-bottom: 3px;
  }
  .info-value { font-size: 13px; font-weight: 500; color: #5e5873; }
}

.section-head {
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.5px; color: #b9b9c3;
  padding-bottom: 8px; border-bottom: 0.5px solid #ebe9f1; margin-bottom: 12px;
}

// ── Weight cards ───────────────────────────────────────────────────────────
.weight-card {
  border-radius: 8px; padding: 11px 13px; margin-bottom: 10px;
  border-left: 3px solid transparent; background: #f8f8f8;
  &.wc-blue  { border-left-color: #185fa5; }
  &.wc-amber { border-left-color: #ef9f27; }
  &.wc-green { border-left-color: #639922; }
  &.wc-red   { border-left-color: #e24b4a; }
  .wc-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; color: #b9b9c3; margin-bottom: 4px; }
  .wc-value { font-size: 17px; font-weight: 500; color: #5e5873; }
}

// ── Variance cards ─────────────────────────────────────────────────────────
.variance-card {
  border-radius: 8px; padding: 12px 14px; border: 0.5px solid; margin-bottom: 10px;
  &.vc-warn    { background: #faeeda; border-color: #fac775; }
  &.vc-danger  { background: #fcebeb; border-color: #f7c1c1; }
  &.vc-success { background: #eaf3de; border-color: #c0dd97; }
  .vc-title { font-size: 11px; font-weight: 500; margin-bottom: 3px; }
  .vc-value { font-size: 16px; font-weight: 500; }
  .vc-sub   { font-size: 11px; margin-top: 3px; opacity: 0.8; color: inherit; }
}

// ── Auth log ───────────────────────────────────────────────────────────────
.log-entry {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 0; border-bottom: 0.5px solid #ebe9f1;
  &:last-child { border-bottom: none; }
  .log-name { font-size: 12px; font-weight: 600; color: #5e5873; }
  .log-role { font-size: 11px; font-weight: 400; color: #b9b9c3; }
  .log-time { font-size: 11px; color: #b9b9c3; margin-top: 1px; }
  .log-note { font-size: 12px; color: #6e6b7b; margin-top: 3px; }
}

// ── Reason field ───────────────────────────────────────────────────────────
.reason-block { margin-top: 4px; }
.reason-label {
  display: flex; align-items: center; gap: 4px;
  font-size: 12px; font-weight: 500; color: #5e5873; margin-bottom: 6px;
}
.required-star { color: #a32d2d; font-size: 14px; line-height: 1; }
.reason-hint   { font-size: 11px; font-weight: 400; color: #b9b9c3; }
.reason-error-msg {
  display: flex; align-items: center; gap: 4px;
  font-size: 11px; color: #a32d2d; margin-top: 5px;
}

table { font-size: 13px; }

// ── Checkbox column ────────────────────────────────────────────────────────
::v-deep table thead th:first-child,
::v-deep table tbody td:first-child {
  text-align: center; vertical-align: middle;
  padding-left: 12px; padding-right: 12px;
}
.checkbox-indeterminate { accent-color: #28a745; }

// ── Row tint for pending bales breaching tolerance ──────────────────────────
.excess-table {
  ::v-deep tr.row-breach td {
    background: rgba(226, 75, 74, 0.06) !important;
    box-shadow: inset 3px 0 0 #e24b4a;
  }
  ::v-deep tbody tr:hover td { background: rgba(24, 95, 165, 0.04) !important; }
}
</style>
